import { useState } from 'react'

import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui/Button'

import { emptyPatternCells, hasMarkedCell, patternFromCells } from './totemPattern'

type TotemPatternGridProps = {
  errorMessage?: string | null
  busy?: boolean
  onSubmit: (pattern: string) => void
}

export function TotemPatternGrid({ errorMessage, busy, onSubmit }: TotemPatternGridProps) {
  const { t } = useTranslation()
  const [cells, setCells] = useState<boolean[]>(emptyPatternCells)

  const toggleCell = (index: number) => {
    setCells(prev => prev.map((cell, i) => (i === index ? !cell : cell)))
  }

  const clear = () => setCells(emptyPatternCells())

  const submit = () => onSubmit(patternFromCells(cells))

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="m-0 text-sm font-bold text-gray-900">{t('quests.totem.title')}</p>
      <p className="m-0 text-xs text-gray-500">{t('quests.totem.instruction')}</p>

      <div className="grid grid-cols-3 gap-3">
        {cells.map((marked, index) => (
          <button
            key={index}
            type="button"
            aria-pressed={marked}
            onClick={() => toggleCell(index)}
            className={
              marked
                ? 'h-16 w-16 rounded-xl border-0 bg-[var(--color-brand)]'
                : 'h-16 w-16 rounded-xl border border-gray-200 bg-white'
            }
          />
        ))}
      </div>

      {errorMessage && (
        <p role="alert" className="m-0 text-xs text-red-600">
          {errorMessage}
        </p>
      )}

      <div className="flex gap-2">
        <Button variant="ghost" size="md" onClick={clear} disabled={busy}>
          {t('quests.totem.clear')}
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={submit}
          disabled={busy || !hasMarkedCell(cells)}
          loading={busy}
        >
          {t('quests.totem.confirm')}
        </Button>
      </div>
    </div>
  )
}
